import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useEffect, useMemo } from "react";
import { NotExist } from "../../../layouts/NotData";
import {
  Button,
  Card,
  cn,
  Flex,
  Loading,
  SecureCopy,
  Tag,
  Typography,
} from "djuno-design";
import { AiDeploysAttachedDataUrl, AiDeploysUrl } from "../../../../utils/urls";
import { useNavigate } from "react-router-dom";
import {
  selectAiApp,
  selectAiAppLoading,
} from "../../../../store/ai/deploys/appSlice";
import CustomLink from "../../../general/CustomLink";
import { AiAppStatusTag } from "../../../../pages/ai-deploys/AiAppsPage";
import { formatTimestamp } from "../../../../utils/date";
import CheckIcon from "./../../../../assets/icons/check.svg?react";
import ArrowIcon from "./../../../../assets/icons/arrow-top-right-on-square.svg?react";
import EditIcon from "./../../../../assets/icons/pencil-square.svg?react";
import { humanizeSize } from "../../../../utils/file";
import { AiAppStatusState } from "../../../../types/ai";
import {
  getAiResourceFlavorsAsync,
  selectAiResourceFlavors,
} from "../../../../store/ai/notebooks/notebooksSlice";
import {
  handleToggleAiAppImageModal,
  handleToggleAiAppScalingModal,
} from "../../../../store/ai/deploys/appsSlice";
import AiAppScalingModal from "./AiAppScalingModal";
import AiAppImageModal from "./AiAppImageModal";

const AiAppInformationTab = () => {
  const app = useAppSelector(selectAiApp);
  const appLoading = useAppSelector(selectAiAppLoading);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const flavors = useAppSelector(selectAiResourceFlavors);

  useEffect(() => {
    if (app) dispatch(getAiResourceFlavorsAsync({ region: app.spec.region }));
  }, [dispatch, app]);

  const flavorData = useMemo(() => {
    return flavors.find((f) => f.id === app?.spec.resources.flavor);
  }, [app, flavors]);

  return (
    <>
      {app && !appLoading && (
        <Flex direction="col" className="gap-5">
          <Card title="General information">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    App name
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.name}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Docker Image
                  </Typography.Title>
                  {app.spec.image && (
                    <SecureCopy
                      type="copy"
                      text={app.spec.image}
                      uiSize="small"
                      className="!max-w-md"
                    />
                  )}
                  <Button
                    uiType="light"
                    uiSize="small"
                    className="mt-2"
                    onClick={() =>
                      dispatch(
                        handleToggleAiAppImageModal({
                          isOpen: true,
                          app,
                        })
                      )
                    }
                  >
                    Update image
                    <EditIcon className="w-4 aspect-square flex-shrink-0" />
                  </Button>
                  <AiAppImageModal />
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Access URL (HTTP endpoint)
                  </Typography.Title>
                  {app.status.url && (
                    <SecureCopy
                      type="copy"
                      text={app.status.url}
                      uiSize="small"
                      className="!max-w-md"
                    />
                  )}
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Port
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.defaultHttpPort}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Attached containers
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.volumes.length} container(s)
                  </Typography.Text>
                  <CustomLink
                    to={AiDeploysAttachedDataUrl(app.id)}
                    className="!text-sm flex items-center gap-1"
                  >
                    View attached data
                    <ArrowIcon className="w-4 flex-shrink-0" />
                  </CustomLink>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Tags
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.labels !== null &&
                      Object.keys(app.spec.labels).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {Object.keys(app.spec.labels).map((key) => (
                            <Tag key={key}>
                              {
                                app.spec.labels![
                                  key as keyof typeof app.spec.labels
                                ]
                              }
                            </Tag>
                          ))}
                        </div>
                      )}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Region
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.region}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Lifecycle">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Status
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    <AiAppStatusTag state={app.status.state} />
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Creation date
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {
                      formatTimestamp(app.createdAt, "D MMM YYYY HH:mm", {
                        isUTC: false,
                      }).datetime
                    }
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Timeline
                  </Typography.Title>
                  {app.status.history.map((history, index) => (
                    <Typography.Text
                      key={index}
                      className="!text-xs flex items-center gap-1 mb-1"
                      uiType={
                        app.status.history.length === index + 1
                          ? undefined
                          : "secondary"
                      }
                    >
                      <CheckIcon
                        className={cn("w-4 flex-shrink-0 ", {
                          "text-primary-300":
                            app.status.history.length === index + 1,
                        })}
                      />
                      <AiAppStatusTag
                        state={history.state as AiAppStatusState}
                      />
                      :{"  "}
                      {
                        formatTimestamp(history.date, "D MMM YYYY HH:mm:ss", {
                          isUTC: false,
                        }).datetime
                      }
                    </Typography.Text>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card title="Resources">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Flex items="center" className="gap-1">
                    <Typography.Title level={6} className="!text-sm">
                      Scaling
                    </Typography.Title>
                    <Button
                      uiType="light"
                      uiSize="small"
                      onClick={() =>
                        dispatch(
                          handleToggleAiAppScalingModal({
                            isOpen: true,
                            app,
                          })
                        )
                      }
                    >
                      <EditIcon className="w-4 aspect-square flex-shrink-0" />
                    </Button>
                  </Flex>
                  <AiAppScalingModal />
                  {app.spec.scalingStrategy?.fixed && (
                    <Typography.Text className="!text-sm flex items-center gap-1">
                      <Flex direction="col" className="w-full gap-1">
                        <Flex className="w-full">Fixed scaling</Flex>
                        <Flex className="w-full">
                          <ul className="block list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                            <li>
                              Replicas:{" "}
                              {app.spec.scalingStrategy.fixed.replicas}
                            </li>
                          </ul>
                        </Flex>
                      </Flex>
                    </Typography.Text>
                  )}

                  {app.spec.scalingStrategy?.automatic && (
                    <Typography.Text className="!text-sm flex items-center gap-1">
                      <Flex direction="col" className="w-full gap-1">
                        <Flex className="w-full">Automatic scaling</Flex>
                        <Flex className="w-full">
                          <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                            <li>
                              Minimum number of replicas:{" "}
                              {app.spec.scalingStrategy.automatic.replicasMin}
                            </li>
                            <li>
                              Maximum number of replicas:{" "}
                              {app.spec.scalingStrategy.automatic.replicasMax}
                            </li>
                            <li>
                              Metric monitored:{" "}
                              {app.spec.scalingStrategy.automatic.resourceType}
                            </li>
                            <li>
                              Trigger threshold:{" "}
                              {
                                app.spec.scalingStrategy.automatic
                                  .averageUsageTarget
                              }
                            </li>
                          </ul>
                        </Flex>
                      </Flex>
                    </Typography.Text>
                  )}
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Resources
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.resources.cpu || app.spec.resources.gpu || "1"} ×{" "}
                    {app.spec.resources.flavor.toUpperCase()}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    GPU/CPU/RAM per resource
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {app.spec.resources.cpu || app.spec.resources.gpu || "1"} ×{" "}
                    {flavorData?.description.toUpperCase()}
                  </Typography.Text>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {humanizeSize(app.spec.resources.memory).join("B")} RAM
                    (CPU)
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Local storage per resource
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {humanizeSize(app.spec.resources.ephemeralStorage, {
                      binaryBaseValue: 1000,
                    }).join("B")}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </Card>
        </Flex>
      )}

      {/* empty service */}
      {app === null && !appLoading && (
        <div className="h-full flex items-center justify-center px-4">
          <div className="flex flex-col items-center">
            <div className="text-center space-y-1">
              <NotExist
                url={() => navigate(AiDeploysUrl)}
                text="The app does not exist"
              />
            </div>
          </div>
        </div>
      )}

      {appLoading && (
        <Flex items="center" justify="center" className="w-full !min-h-[300px]">
          <Loading borderSize={2} />
        </Flex>
      )}
    </>
  );
};

export default AiAppInformationTab;
